import React, { ReactElement, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Image } from 'components/lib';
import { COLORS, FONT, SkeletonBlock, initialsOf } from '../shared';

export interface SchoolHeaderProps {
  name?: string;
  bannerUrl?: string;

  iconUrl?: string;
  loading?: boolean;
}

const BANNER_HEIGHT = 325;
const CREST_SIZE = 80;

export const SchoolHeader = ({
  name,
  bannerUrl,
  iconUrl,
  loading = false,
}: SchoolHeaderProps): ReactElement => {
  const [bannerError, setBannerError] = useState(false);
  const [iconError, setIconError] = useState(false);

  const showBanner = bannerUrl && !bannerError;
  const showIcon = iconUrl && !iconError;

  return (
    <Box component="header" sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative', mb: `${CREST_SIZE - 39}px` }}>
        <Box
          sx={{
            height: `${BANNER_HEIGHT}px`,
            width: '100%',
            backgroundColor: COLORS.navy,
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <SkeletonBlock height="100%" />
          ) : (
            showBanner && (
              <Image
                src={bannerUrl ?? ''}
                alt={name ? `${name} banner` : 'School banner'}
                width={800}
                priority
                onError={() => setBannerError(true)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  pointerEvents: 'none',
                }}
              />
            )
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            left: '20px',
            top: `${BANNER_HEIGHT - 39}px`,
            width: `${CREST_SIZE}px`,
            height: `${CREST_SIZE}px`,
            backgroundColor: COLORS.white,
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(20,31,51,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {loading ? (
            <SkeletonBlock width="60%" height="60%" radius={6} />
          ) : showIcon ? (
            <Image
              src={iconUrl ?? ''}
              alt={name ? `${name} crest` : 'School crest'}
              width={CREST_SIZE}
              height={CREST_SIZE}
              onError={() => setIconError(true)}
              style={{
                width: '74%',
                height: '74%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography
              aria-hidden
              sx={{
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: '28px',
                color: COLORS.navy,
              }}
            >
              {initialsOf(name)}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ px: '20px', py: '16px', overflow: 'hidden' }}>
        {loading ? (
          <SkeletonBlock width="70%" height={24} />
        ) : (
          <Typography
            component="h1"
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '24px',
              color: COLORS.headingDark,
              m: 0,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: { xs: 1, md: 2 },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
